import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { IMAGES, waLink, WA_DEFAULT_MSG, scrollToSection } from "@/lib/site";
import { EASE } from "@/components/Reveal";

const LINES = [
  { text: "WE MOVE PARCELS.", cls: "text-white" },
  { text: "WE RUN BAKKIES.", cls: "text-white" },
  { text: "NATIONWIDE.", cls: "text-[#FF6B00]" },
];

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.13, duration: 0.95, ease: EASE },
  }),
};

const SPECS = [
  { value: "09", label: "Provinces Covered" },
  { value: "48–72H", label: "Mobilisation on Signed Scope" },
  { value: "POD", label: "Captured on Every Manifest" },
  { value: "B2B", label: "Contract-First Operations" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0.25]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[100svh] flex-col overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={IMAGES.hero}
          alt=""
          className="h-[115%] w-full object-cover object-center"
        />
      </motion.div>
      <div
        className="absolute inset-0 bg-gradient-to-r from-[#080C14] via-[#080C14]/80 to-[#080C14]/30"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-[#080C14] via-transparent to-[#080C14]/70"
        aria-hidden="true"
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pb-14 pt-32 lg:px-8"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
          className="flex items-center gap-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF6B00] sm:text-xs"
        >
          <span className="live-dot inline-block h-2 w-2 rounded-full bg-[#10B981]" />
          Nationwide Courier Contractor — South Africa
        </motion.p>

        <h1 className="mt-6 font-display text-[13vw] font-black uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl xl:text-8xl">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.cls}`}
                variants={lineVariant}
                custom={i}
                initial="hidden"
                animate="visible"
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          NGCONDE Courier Service provides contracted parcel delivery and dedicated
          bakkie transport capacity for courier companies, e-commerce businesses,
          retailers, warehouses, distributors and corporate businesses —
          owner-managed, POD-verified, across all nine provinces.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
          className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <button
            onClick={() => scrollToSection("enquiry")}
            data-testid="hero-cta-become-partner"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B00] px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#080C14] transition-all hover:bg-[#E05E00] hover:shadow-[0_0_36px_rgba(255,107,0,0.45)] active:scale-95"
          >
            Become a Partner
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            href={waLink(WA_DEFAULT_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#25D366]/60 bg-[#080C14]/40 px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#25D366] backdrop-blur transition-all hover:bg-[#25D366] hover:text-[#080C14] active:scale-95"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp 071 243 1886
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.1 }}
        className="relative z-10 border-t border-white/10 bg-[#080C14]/70 backdrop-blur"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
          {SPECS.map((s, i) => (
            <div
              key={s.label}
              className={`border-white/10 px-5 py-5 lg:px-8 ${i % 2 === 1 ? "border-l" : ""} ${i > 1 ? "border-t lg:border-t-0" : ""} ${i > 0 ? "lg:border-l" : ""}`}
            >
              <p className="font-display text-2xl font-black tracking-tighter text-white md:text-3xl">
                {s.value}
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
